/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Stack, Typography, TextField, CircularProgress, Radio, InputAdornment, Skeleton, Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';
import { searchDentallyPatients, exportLeadToDentally } from '../../utils/dentallyApi';
import { patchLeadMetadata } from '../../redux/slices/lead';
import { dispatch } from '../../redux/store';
import { callApi } from '../../utils/apiCall';
import {
    journeyPatientResultStyle,
    journeyLinkedBadgeStyle,
} from '../../styles/DentallyBookingStyle';

// Robust patient array extraction — handles multiple API response shapes
const extractPatients = (res) => {
    const d = res?.data;
    if (!d) return [];
    // Dentally API shape
    if (Array.isArray(d.patients)) return d.patients;
    // Django REST shape
    if (Array.isArray(d.results)) return d.results;
    // Direct array
    if (Array.isArray(d)) return d;
    // Wrapped in data
    if (Array.isArray(d.data)) return d.data;
    return [];
};

const getPatientName = (patient) => {
    if (patient.name) return patient.name;
    return [patient.title, patient.first_name, patient.last_name]
        .filter(Boolean).join(' ') || `Patient #${patient.id}`;
};

const getPatientDetail = (patient) => {
    const parts = [];
    if (patient.email_address || patient.email) parts.push(patient.email_address || patient.email);
    if (patient.mobile_phone || patient.phone_number || patient.mobile) {
        parts.push(patient.mobile_phone || patient.phone_number || patient.mobile);
    }
    if (patient.date_of_birth) parts.push(`DOB: ${patient.date_of_birth}`);
    return parts.join(' \u2022 ');
};

const DentallyPatientMapper = ({ leadId, practiceId, leadMetadata, onPatientMapped }) => {
    const [mode, setMode] = useState('checking'); // 'checking' | 'linked' | 'search'
    const [linkedPatientId, setLinkedPatientId] = useState(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [isLinking, setIsLinking] = useState(false);
    const debounceRef = useRef(null);

    // Export state
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState('');

    // Cached lead data for pre-export validation
    const [cachedLead, setCachedLead] = useState(null);

    // Track whether we already resolved to 'linked' to avoid re-checking
    const resolvedRef = useRef(false);

    // Helper: check a metadata array for dentally_patient_id
    const findDentallyId = (metadata) => {
        if (!Array.isArray(metadata)) return null;
        const entry = metadata.find(m => m.key === 'dentally_patient_id');
        return entry?.value || null;
    };

    // On mount / when leadMetadata prop changes — check for existing link
    useEffect(() => {
        if (resolvedRef.current) return;
        const existing = findDentallyId(leadMetadata);
        if (existing) {
            resolvedRef.current = true;
            setLinkedPatientId(existing);
            setMode('linked');
            onPatientMapped(existing);
            return;
        }
        // Only fall through to search if we haven't started an API check yet
        if (mode === 'checking') return;
        setMode('search');
    }, [leadMetadata]);

    // Fallback: fetch lead metadata directly from API when props don't have it
    useEffect(() => {
        if (resolvedRef.current || !leadId || !practiceId) {
            if (!resolvedRef.current) setMode('search');
            return;
        }
        let cancelled = false;
        const fetchMetadata = async () => {
            try {
                const res = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`);
                if (cancelled) return;
                // Cache full lead for pre-export validation
                if (res?.data) setCachedLead(res.data);
                const metadata = res?.data?.lead_metadata;
                const existing = findDentallyId(metadata);
                if (existing) {
                    resolvedRef.current = true;
                    setLinkedPatientId(existing);
                    setMode('linked');
                    onPatientMapped(existing);
                } else {
                    setMode('search');
                }
            } catch {
                if (!cancelled) setMode('search');
            }
        };
        fetchMetadata();
        return () => { cancelled = true; };
    }, [leadId, practiceId]);

    // Debounced search
    const handleSearchChange = useCallback((e) => {
        const val = e.target.value;
        setSearchQuery(val);
        setSelectedPatient(null);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (val.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await searchDentallyPatients(practiceId, val.trim());
                setSearchResults(extractPatients(res));
            } catch {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 500);
    }, [practiceId]);

    // Link selected patient
    const handleLinkPatient = async () => {
        if (!selectedPatient) return;
        setIsLinking(true);
        try {
            const patientId = String(selectedPatient.id);
            await dispatch(patchLeadMetadata(practiceId, leadId, [
                { key: 'dentally_patient_id', value: patientId },
            ]));
            setLinkedPatientId(patientId);
            setMode('linked');
            onPatientMapped(patientId);
        } catch {
            // Error handled by thunk
        } finally {
            setIsLinking(false);
        }
    };

    // Pre-export field validation — Dentally requires these to create a patient
    const getMissingDentallyFields = () => {
        const lead = cachedLead;
        const missing = [];
        if (!lead?.salutation) missing.push('Title (Mr / Mrs / Ms / Miss / Dr)');
        if (!lead?.dob) missing.push('Date of birth');
        if (!lead?.lead_contact?.email) missing.push('Email address');
        if (!lead?.lead_address?.postcode) missing.push('Postcode');
        return missing;
    };

    // Export to Dentally (creates patient + links)
    const handleExportToDentally = async () => {
        setExportError('');

        // Validate required fields before attempting export
        const missing = getMissingDentallyFields();
        if (missing.length > 0) {
            setExportError(
                `Dentally requires the following fields to create a patient record. Please go back to Personal Details and add them:\n• ${missing.join('\n• ')}`
            );
            return;
        }

        setIsExporting(true);
        try {
            const res = await exportLeadToDentally(leadId);
            if (res?.ok || res?.status === 200 || res?.status === 201) {
                const patientId = String(
                    res.data?.dentally_patient_id || res.data?.patient_id || res.data?.id || ''
                );
                if (patientId) {
                    setLinkedPatientId(patientId);
                    setMode('linked');
                    onPatientMapped(patientId);
                } else {
                    setExportError('Export succeeded but no patient ID was returned. Please search for the patient manually.');
                }
            } else {
                const errMsg = res?.data?.detail
                    || res?.data?.message
                    || (Array.isArray(res?.data?.non_field_errors) && res.data.non_field_errors[0])
                    || res?.data?.error
                    || 'Failed to export patient to Dentally. Please check the patient details and try again.';
                setExportError(errMsg);
            }
        } catch {
            setExportError('An unexpected error occurred while exporting to Dentally.');
        } finally {
            setIsExporting(false);
        }
    };

    // ── Checking state (brief, on mount) ─────────────────────────────────
    if (mode === 'checking') {
        return (
            <Box sx={{ py: 2 }}>
                <Stack direction='row' alignItems='center' spacing={1.5}>
                    <CircularProgress size={18} />
                    <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: 13 }}>
                        Checking Dentally patient link...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // ── Linked state ─────────────────────────────────────────────────────
    if (mode === 'linked' && linkedPatientId) {
        return (
            <Box sx={journeyLinkedBadgeStyle}>
                <Box sx={{
                    width: 28, height: 28, borderRadius: '50%',
                    bgcolor: 'rgba(34,197,94,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Iconify icon='mdi:check-bold' width={16} sx={{ color: 'success.main' }} />
                </Box>
                <Box>
                    <Typography variant='body2' sx={{ fontWeight: 700, fontSize: 13, color: 'success.dark' }}>
                        Linked to Dentally Patient #{linkedPatientId}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: 11 }}>
                        Patient record is connected and ready
                    </Typography>
                </Box>
            </Box>
        );
    }

    // ── Search / Export state ────────────────────────────────────────────
    return (
        <Box>
            {/* Search existing patients */}
            <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: 13, mb: 1.5 }}>
                Search for an existing Dentally patient or create a new one
            </Typography>
            <TextField
                fullWidth
                size='small'
                placeholder='Search by name, email, or phone...'
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        bgcolor: 'rgba(145,158,171,0.04)',
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            <Iconify icon='mdi:magnify' width={18} sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                    ),
                    endAdornment: isSearching ? (
                        <InputAdornment position='end'>
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : null,
                }}
            />

            {/* Search results */}
            {searchResults.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1.5, maxHeight: 180, overflowY: 'auto' }}>
                    {searchResults.map((patient) => {
                        const isSelected = selectedPatient?.id === patient.id;
                        const name = getPatientName(patient);
                        const detail = getPatientDetail(patient);
                        return (
                            <Box
                                key={patient.id}
                                onClick={() => setSelectedPatient(patient)}
                                sx={journeyPatientResultStyle(isSelected)}
                            >
                                <Stack direction='row' alignItems='center' spacing={1}>
                                    <Radio
                                        size='small'
                                        checked={isSelected}
                                        sx={{ p: 0, color: 'text.disabled' }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant='body2' noWrap sx={{ fontWeight: 600, fontSize: 13 }}>
                                            {name}
                                        </Typography>
                                        {detail && (
                                            <Typography variant='caption' noWrap sx={{ color: 'text.disabled', fontSize: 10 }}>
                                                #{patient.id} &bull; {detail}
                                            </Typography>
                                        )}
                                        {!detail && (
                                            <Typography variant='caption' sx={{ color: 'text.disabled', fontSize: 10 }}>
                                                #{patient.id}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {/* Searching skeleton */}
            {isSearching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                    {[0, 1, 2].map((i) => (
                        <Box key={i} sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid rgba(145,158,171,0.12)' }}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                                <Skeleton variant='circular' width={20} height={20} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant='text' width='55%' height={16} />
                                    <Skeleton variant='text' width='35%' height={12} />
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}

            {/* No results found */}
            {!isSearching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                <Typography variant='caption' sx={{ color: 'text.disabled', display: 'block', mb: 1.5, fontSize: 11 }}>
                    No matching patients found in Dentally
                </Typography>
            )}

            {/* Link button */}
            {selectedPatient && (
                <LoadingButton
                    fullWidth
                    variant='contained'
                    size='small'
                    loading={isLinking}
                    onClick={handleLinkPatient}
                    startIcon={<Iconify icon='mdi:link-variant' />}
                    sx={{
                        mb: 1.5,
                        borderRadius: '10px',
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
                    }}
                >
                    Link Patient: {getPatientName(selectedPatient)}
                </LoadingButton>
            )}

            {/* Divider with OR */}
            <Stack direction='row' alignItems='center' spacing={1.5} sx={{ my: 1.5 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
                <Typography variant='caption' sx={{ color: 'text.disabled', fontWeight: 700, fontSize: 10, letterSpacing: '1px' }}>
                    OR
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
            </Stack>

            {/* Export error */}
            {exportError && (
                <Alert
                    severity='error'
                    sx={{ mb: 1.5, borderRadius: '10px', fontSize: 12, whiteSpace: 'pre-line' }}
                    onClose={() => setExportError('')}
                >
                    {exportError}
                </Alert>
            )}

            {/* Export to Dentally */}
            <LoadingButton
                fullWidth
                variant='outlined'
                loading={isExporting}
                onClick={handleExportToDentally}
                startIcon={<Iconify icon='mdi:account-plus-outline' />}
                sx={{
                    borderRadius: '10px',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderColor: 'rgba(145,158,171,0.3)',
                    color: 'text.primary',
                    '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'rgba(24,144,255,0.04)',
                    },
                }}
            >
                Export Lead To Dentally (Create New Patient)
            </LoadingButton>
        </Box>
    );
};

DentallyPatientMapper.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadMetadata: PropTypes.array,
    onPatientMapped: PropTypes.func.isRequired,
};

DentallyPatientMapper.defaultProps = {
    leadMetadata: [],
};

export default DentallyPatientMapper;
